import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GripVertical, Lock, MinusCircle, PlusCircle, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { DEFAULT_SLOTS, FILTER_DEFS, type FilterSlot } from './filter-rail'

/**
 * "Chỉnh sửa bộ lọc" kiểu Lark: danh sách Hiển thị (kéo để đổi thứ tự, − để
 * ẩn) và danh sách Ẩn (+ để hiện lại). "Tất cả" luôn đứng đầu, không ẩn được.
 */
export function FilterSettingsDialog({
  open,
  onOpenChange,
  value,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  value: FilterSlot[]
  onSave: (slots: FilterSlot[]) => void
}) {
  const { t } = useTranslation('convList')
  const [shown, setShown] = useState<FilterSlot[]>(value)
  const [dragging, setDragging] = useState<FilterSlot | null>(null)

  // Mỗi lần mở lại thì bắt đầu từ cấu hình đang dùng (bỏ thay đổi chưa lưu).
  useEffect(() => {
    if (open) setShown(value)
  }, [open, value])

  const hidden = DEFAULT_SLOTS.filter((s) => !shown.includes(s))

  const moveBefore = (item: FilterSlot, target: FilterSlot) => {
    if (item === target || target === 'all') return
    setShown((cur) => {
      const next = cur.filter((x) => x !== item)
      next.splice(next.indexOf(target), 0, item)
      return next
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('filterSettings.title')}</DialogTitle>
          <DialogDescription>{t('filterSettings.desc')}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          <section>
            <h3 className="mb-2 text-sm font-medium">{t('filterSettings.shown')}</h3>
            <ul className="divide-y rounded-lg border">
              {shown.map((slot) => {
                const def = FILTER_DEFS[slot]
                const fixed = slot === 'all'
                return (
                  <li
                    key={slot}
                    draggable={!fixed}
                    onDragStart={() => setDragging(slot)}
                    onDragEnd={() => setDragging(null)}
                    onDragOver={(e) => {
                      e.preventDefault()
                      if (dragging) moveBefore(dragging, slot)
                    }}
                    className={cn(
                      'flex h-11 items-center gap-3 bg-card px-3 text-sm',
                      dragging === slot && 'opacity-50',
                      !fixed && 'cursor-grab active:cursor-grabbing',
                    )}
                  >
                    {fixed ? (
                      <Lock className="h-4 w-4 text-muted-foreground/50" aria-label={t('filterSettings.fixed')} />
                    ) : (
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                    )}
                    <button
                      type="button"
                      disabled={fixed}
                      aria-label={t('filterSettings.hide', { label: def.label })}
                      onClick={() => setShown((cur) => cur.filter((x) => x !== slot))}
                      className="text-destructive disabled:text-muted-foreground/40"
                    >
                      <MinusCircle className="h-5 w-5" />
                    </button>
                    <def.icon className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1">{def.label}</span>
                    {def.hint && <span className="hidden text-xs text-muted-foreground sm:inline">{def.hint}</span>}
                  </li>
                )
              })}
            </ul>
          </section>

          {hidden.length > 0 && (
            <section>
              <h3 className="mb-2 text-sm font-medium">{t('filterSettings.hiddenTitle')}</h3>
              <ul className="divide-y rounded-lg border">
                {hidden.map((slot) => {
                  const def = FILTER_DEFS[slot]
                  return (
                    <li key={slot} className="flex h-11 items-center gap-3 bg-card px-3 text-sm">
                      <span className="w-4" />
                      <button
                        type="button"
                        aria-label={t('filterSettings.show', { label: def.label })}
                        onClick={() => setShown((cur) => [...cur, slot])}
                        className="text-success"
                      >
                        <PlusCircle className="h-5 w-5" />
                      </button>
                      <def.icon className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1 text-muted-foreground">{def.label}</span>
                    </li>
                  )
                })}
              </ul>
            </section>
          )}
        </div>

        <DialogFooter className="sm:justify-between">
          <Button variant="ghost" onClick={() => setShown(DEFAULT_SLOTS)}>
            <RotateCcw /> {t('shared.default')}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('filterSettings.discard')}
            </Button>
            <Button
              onClick={() => {
                onSave(shown)
                onOpenChange(false)
              }}
            >
              {t('common:actions.save')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
